import { useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useTranslation } from 'react-i18next';

export interface SortableListProps<T> {
  readonly items: readonly T[];
  /** A stable identity per item — the React key and the drag payload. */
  readonly itemKey: (item: T) => string;
  /** The row's content, after the drag handle and before the move buttons. */
  readonly renderItem: (item: T, index: number) => ReactNode;
  /** Called with the dragged item's index and the index it was dropped on. */
  readonly onMove: (from: number, to: number) => void;
  readonly readOnly?: boolean;
  /** The list's accessible name. */
  readonly label: string;
  /** Per-item accessible name used in the move buttons: "Kategoriedeckung". */
  readonly itemLabel: (item: T) => string;
  /**
   * An extra drag payload `[type, value]` set beside the item key, so a drop target OUTSIDE this
   * list can recognise the item (the Data Suite's shelves move a token from one shelf to another,
   * SDD-024 U-2). Reordering inside the list is unaffected.
   */
  readonly dragData?: (item: T) => readonly [string, string];
  /** Hide the up/down buttons — a caller that offers the moves in its own menu (U-2). */
  readonly hideMoveButtons?: boolean;
}

/**
 * A list the operator reorders by dragging (`drag_indicator`, as in EIS v1) — and, because a drag is
 * a pointer gesture nobody can perform from a keyboard or a screen reader, by the up/down buttons on
 * every row (WCAG 2.1.1). Native HTML5 drag and drop: no library (CLAUDE.md rule 1).
 *
 * Used by the claim position columns and the calculation order of `tarif_deckung` (ui-spec §3.2,
 * §3.3c). Presentational: the caller owns the order and decides what a move means.
 *
 * Alt+↑ / Alt+↓ on anything focused inside a row moves the row (SDD-024 U-2, prompt 60): the
 * keyboard twin of the drag, for lists whose rows hold their own focusable controls.
 */
export function SortableList<T>({
  items,
  itemKey,
  renderItem,
  onMove,
  readOnly = false,
  label,
  itemLabel,
  dragData,
  hideMoveButtons = false,
}: SortableListProps<T>): ReactNode {
  const { t } = useTranslation('patterns');
  const [dragging, setDragging] = useState<number | undefined>(undefined);
  const [over, setOver] = useState<number | undefined>(undefined);

  return (
    <List dense aria-label={label} sx={{ py: 0 }}>
      {items.map((item, index) => {
        const name = itemLabel(item);
        return (
          <ListItem
            key={itemKey(item)}
            draggable={!readOnly}
            onDragStart={(event) => {
              setDragging(index);
              // Firefox starts no drag without data; a synthetic event may carry no transfer at all.
              const transfer = event.dataTransfer as DataTransfer | null;
              if (transfer !== null) {
                transfer.effectAllowed = 'move';
                transfer.setData('text/plain', itemKey(item));
                const extra = dragData?.(item);
                if (extra !== undefined) transfer.setData(extra[0], extra[1]);
              }
            }}
            onKeyDown={(event) => {
              if (readOnly || !event.altKey) return;
              if (event.key === 'ArrowUp' && index > 0) {
                event.preventDefault();
                onMove(index, index - 1);
              } else if (event.key === 'ArrowDown' && index < items.length - 1) {
                event.preventDefault();
                onMove(index, index + 1);
              }
            }}
            onDragOver={(event) => {
              if (dragging === undefined) return;
              event.preventDefault();
              setOver(index);
            }}
            onDrop={(event) => {
              event.preventDefault();
              if (dragging !== undefined && dragging !== index) onMove(dragging, index);
              setDragging(undefined);
              setOver(undefined);
            }}
            onDragEnd={() => {
              setDragging(undefined);
              setOver(undefined);
            }}
            sx={{
              border: 1,
              borderColor: over === index ? 'primary.main' : 'divider',
              borderRadius: 1,
              mb: 0.5,
              bgcolor: dragging === index ? 'action.selected' : 'background.paper',
              cursor: readOnly ? 'default' : 'grab',
            }}
            secondaryAction={
              readOnly || hideMoveButtons ? undefined : (
                <Stack direction="row">
                  <Tooltip title={t('sortable.moveUp', { item: name })}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={t('sortable.moveUp', { item: name })}
                        disabled={index === 0}
                        onClick={() => {
                          onMove(index, index - 1);
                        }}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('sortable.moveDown', { item: name })}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={t('sortable.moveDown', { item: name })}
                        disabled={index === items.length - 1}
                        onClick={() => {
                          onMove(index, index + 1);
                        }}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              )
            }
          >
            {!readOnly && (
              <Box
                component="span"
                aria-hidden
                sx={{ display: 'flex', color: 'text.secondary', mr: 1 }}
              >
                <DragIndicatorIcon fontSize="small" />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0, pr: readOnly || hideMoveButtons ? 0 : 10 }}>
              {renderItem(item, index)}
            </Box>
          </ListItem>
        );
      })}
    </List>
  );
}
